/**
 * Main application layout — Brand Book §3.1
 * bg-main background, 60px horizontal padding desktop, 16px mobile
 * Optional 220px deal sidebar (set via useDealSidebar() in deal-context pages)
 */

import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { DealSidebar } from './DealSidebar';
import { Toast } from './Toast';
import { BottomNav } from './BottomNav';

import { DealWizardModal } from '../../features/deals/components/DealWizardModal';
import { CommandPalette } from './CommandPalette';
import { ChatPanel } from '../../features/chat';
import { useUIStore } from '../../stores/uiStore';
import { useTierUpgradeSync } from '../../hooks/useTierUpgradeSync';
// Breadcrumbs are now rendered inside Header (left side, inline with logo)

export function MainLayout() {
  const hasSidebar = useUIStore((s) => s.dealSidebarCtx !== null);
  const { pathname } = useLocation();

  // Auto-refresh this tab to Pro when checkout completes in another tab.
  useTierUpgradeSync();
  // On mobile, /chat is the entire app surface (see MobileChatGate in App.tsx).
  // Stripping <main>'s mobile vertical padding here keeps ChatPage's
  // `h-[calc(100dvh-3.5rem)]` honest — without it, `py-6 pb-20` adds 104px
  // of overflow on touch devices that lets the input drift up when
  // dragging. Desktop keeps its gutter via the `md:` prefix.
  const isChatRoute = pathname === '/chat';

  return (
    <div className="min-h-dvh bg-surface-main">
      {/* Idle-hide reveal sentinel — 8px invisible strip at the very top
          of the viewport. Any pointer or touch entering it propagates
          mousemove / touchstart to document, which the useIdleHide hook
          in Header listens for. This catches the "park cursor at the
          top edge to peek" gesture even when there's no actual mouse
          movement. z-50 keeps it above the header's z-40 so the strip
          remains the topmost hit-target even when the header is fully
          visible. pointer-events: auto explicitly enabled because some
          ancestors set pointer-events: none. */}
      <div
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 z-50 h-2"
        style={{ pointerEvents: 'auto' }}
      />

      {/* Skip to content — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-orange focus:text-white focus:text-body-sm focus:font-semibold"
      >
        Skip to main content
      </a>

      {/* Header — breadcrumb chain rendered inside, left of nav */}
      <Header />

      {/* Body: optional sidebar + content */}
      <div className="flex">
        {/* Deal sidebar — 220px sticky, desktop only, shown on deal-context pages */}
        <DealSidebar />

        {/* Main Content */}
        <main
          id="main-content"
          className={
            isChatRoute
              ? // /chat: no padding on mobile so ChatPage fills exactly
                // 100dvh − header; desktop keeps the 60px / 24px gutter
                'flex-1 min-w-0 md:px-[60px] md:py-6'
              : hasSidebar
                ? 'flex-1 min-w-0 px-4 lg:px-10 py-6 pb-20 md:pb-6'
                : 'flex-1 min-w-0 px-4 lg:px-[60px] py-6 pb-20 md:pb-6'
          }
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <BottomNav />

      {/* Toast Notifications */}
      <Toast />

      {/* Global Modals */}
      <DealWizardModal />

      {/* Command Palette */}
      <CommandPalette />

      {/* AI Chat — right rail, gated by feature_flags.chat_enabled */}
      <ChatPanel />

    </div>
  );
}
